"use client";

import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import { chartFilename } from "@/lib/tool-kit/charts/layout";

/** The export labels a macro line chart needs; widgets spread these into their strings. */
export const MACRO_CHART_STRINGS = ECON_CHART_STRINGS;

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

/**
 * A line graph of one or more series with the download buttons, data copy and a data
 * table beneath it. The x labels are the rows' own labels, so years stay unformatted.
 */
export function MacroLineFigure({
  id,
  rows,
  names,
  numeric,
  title,
  xTitle,
  yTitle,
  description,
  fallbackName,
  fmt,
  labels,
  markers = rows.length <= 14,
  zero = true,
  showTable = true,
}: {
  id: string;
  rows: LineRow[];
  names: string[];
  numeric: boolean;
  title: string;
  xTitle: string;
  yTitle: string;
  description: string;
  fallbackName: string;
  fmt: Fmt;
  labels: typeof MACRO_CHART_STRINGS;
  markers?: boolean;
  zero?: boolean;
  /** Offer the graph's data as a table beneath it. */
  showTable?: boolean;
}) {
  const text = (value: number | null) =>
    value === null ? "" : fmt(value, { maximumFractionDigits: 4 });
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [xTitle, ...names],
        ...rows.map((row) => [row.label, ...row.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(title, fallbackName)}
      labels={labels}
      note=""
      table={
        showTable
          ? {
              head: [xTitle, ...names],
              rows: rows.map((row) => [row.label, ...row.values.map(text)]),
            }
          : null
      }
      title={title}
    >
      <LineChart
        description={description}
        fmt={fmt}
        id={id}
        label={title}
        markers={markers}
        names={names}
        numeric={numeric}
        rows={rows}
        style={{
          title,
          xTitle,
          yTitle,
          color: "blue",
          valueLabels: false,
          apa: false,
          figureNumber: "1",
          note: "",
        }}
        zero={zero}
      />
    </ChartFigure>
  );
}
